import { computed, ref, useHost, watch, type ComputedRef, type Ref } from "vue";
import type { VideoProps } from "./video.types";
import { videoMediaClasses } from "./video.styles";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";
import { parseBoolAttr } from "@/runtime/shared/attrs";
import { useLazyReveal } from "composables/useLazyReveal";

const SKELETON_BASE = "absolute inset-0 animate-pulse bg-(--color-elevation-200)/50";

export interface UseVideoOptions {
  props: Readonly<VideoProps>;
}

export interface UseVideoReturn {
  mediaClasses: ComputedRef<ReturnType<typeof videoMediaClasses>>;
  wrapperClasses: ComputedRef<string>;
  videoSrc: ComputedRef<string>;
  isAutoplay: ComputedRef<boolean>;
  isMuted: ComputedRef<boolean>;
  isLoop: ComputedRef<boolean>;
  isControls: ComputedRef<boolean>;
  isLoaded: Ref<boolean>;
  showSkeleton: ComputedRef<boolean>;
  skeletonClasses: ComputedRef<string>;
  onLoadedData: () => void;
}

export const useVideo = ({ props }: UseVideoOptions): UseVideoReturn => {
  const host = useHost();
  const dispatch = (name: string): void => {
    if (host) host.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true }));
  };

  const isLazy = computed(() => parseBoolAttr(props.lazy, false));
  const isRounded = computed(() => parseBoolAttr(props.rounded, true));
  const isAutoplay = computed(() => parseBoolAttr(props.autoplay, false));
  const isMuted = computed(() => parseBoolAttr(props.muted, false));
  const isLoop = computed(() => parseBoolAttr(props.loop, false));
  const isControls = computed(() => parseBoolAttr(props.controls, false));
  const isSkeleton = computed(() => parseBoolAttr(props.skeleton, true));

  const isLoaded = ref(false);

  const { isVisible, reattach } = useLazyReveal({ elementRef: host, enabled: isLazy });

  const videoSrc = computed<string>(() => (isLazy.value && !isVisible.value ? "" : props.src));

  const mediaClasses = computed(() => videoMediaClasses({ rounded: isRounded.value ? "true" : "false" }));
  const wrapperClasses = computed(() => mergeClassProp("relative block w-full h-full", props.class));

  const showSkeleton = computed(() => isSkeleton.value && !isLoaded.value);
  const skeletonClasses = computed(() => (isRounded.value ? `${SKELETON_BASE} rounded-lg` : SKELETON_BASE));

  watch(
    () => props.src,
    () => {
      isLoaded.value = false;
      if (!isLazy.value) return;
      isVisible.value = false;
      reattach();
    },
  );

  return {
    mediaClasses,
    wrapperClasses,
    videoSrc,
    isAutoplay,
    isMuted,
    isLoop,
    isControls,
    isLoaded,
    showSkeleton,
    skeletonClasses,
    onLoadedData: () => {
      isLoaded.value = true;
      dispatch("load");
    },
  };
};
